// Locale rules shared by the server (i18n request config, Layout) and the client (LanguagesContext). Pure, so tested.

// The language used when a request has no usable `locale` cookie. Same default as `get_current_locate_server`.
export const DEFAULT_LOCALE = "fa";

// A language code such as "fa", "en", "en-US", "zh-Hant". Anything else (undefined, "", "null", "./x") is not one.
const LOCALE_PATTERN = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

export const is_locale_code = (value) => typeof value === "string" && LOCALE_PATTERN.test(value);

// The cookie value when it is a language code, otherwise the fallback. Requests the middleware skips (a missing
// `/sw.js`, `/apple-touch-icon.png`, a scanner without cookies) have no `locale` cookie; loading `@/dictionaries/undefined`
// then failed and a 404 became a 500 (chawk-bazar, ciseco kept local copies of the i18n config for this).
export const resolve_locale = (value, fallback = DEFAULT_LOCALE) => (is_locale_code(value) ? value : fallback);

// Languages written right to left (base code, region ignored).
const RTL_LANGUAGES = ["ar", "fa", "he", "ur", "ps", "ckb", "dv", "yi", "sd", "ug"];

// "rtl" / "ltr" for a language code; `fallback` for anything that is not one. `ar` was missing from
// locates-direction.json, so the client set `<html dir="undefined">` after hydration on Arabic pages.
export const get_locale_direction = (locale, fallback) => {
	if (!is_locale_code(locale)) return fallback;
	return RTL_LANGUAGES.includes(locale.split("-")[0].toLowerCase()) ? "rtl" : "ltr";
};

/**
 * The request path without its language prefix, as the backend knows it: `/en/x/` → `/x/`, `/en` and `/en/` → `/`.
 * Only a whole leading segment is a prefix (`/faq/` stays `/faq/`, `/x/en/` stays). Used by `init_page_server`,
 * `generate_metadata` and the sites (chawk-bazar / ciseco had copies, one still turning `/faq` into `q`).
 */
export const strip_locale_prefix = (pathname, locale) => {
	if (typeof pathname !== "string" || !locale) return pathname;
	if (pathname === `/${locale}` || pathname === `/${locale}/`) return "/";
	return pathname.startsWith(`/${locale}/`) ? pathname.slice(locale.length + 1) : pathname;
};

/**
 * Like `strip_locale_prefix`, for when the current language is not known: removes a leading segment that is any of
 * `locales` (`/en/cart/` → `/cart/`, `/ar` → `/`). Added for chawk-bazar / ciseco (`strip_known_locale_prefix` in
 * helpers/systemRoutes.js): their `generateMetadata` compares the request path with route names before the `locale`
 * cookie of a first `/en/...` visit is reliable. Only codes that pass `is_locale_code` count.
 */
export const strip_any_locale_prefix = (pathname, locales) => {
	if (typeof pathname !== "string" || !Array.isArray(locales)) return pathname;
	const locale = pathname.split("/")[1];
	return is_locale_code(locale) && locales.includes(locale) ? strip_locale_prefix(pathname, locale) : pathname;
};
